// Full-screen in-call video UI for the user side. Mounts once the lawyer
// accepts, joins the Agora channel via useAgora, and renders remote + local video.
import { useEffect, useRef, useState } from "react";
import { useAgora } from "./useAgora";
import VideoPlayer from "./VideoPlayer";
import ChatPanel from "./ChatPanel";
import brandLogo from "../../assets/images/footer-logo.png";
import "./call.css";

function fmt(total) {
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
}

export default function CallScreen({ call, onEnd }) {
  const {
    status, error, remoteUsers, localVideoTrack,
    micOn, camOn, speakerOn, canFlip, playbackDevices, activeSpeaker,
    toggleMic, toggleCam, toggleSpeaker, flipCamera, setPlayback,
  } = useAgora(call.creds);
  const [seconds, setSeconds] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [warnVisible, setWarnVisible] = useState(false);
  const [spkMenuOpen, setSpkMenuOpen] = useState(false);
  const localRef = useRef(null);
  const warnedRef = useRef(false);
  const endedRef = useRef(false);

  // Paid-plan countdown. When the call has a plan duration we count DOWN and
  // auto-end at zero; otherwise we just count elapsed time up.
  const total = (call.durationMinutes || 0) * 60;
  const remaining = total > 0 ? Math.max(0, total - seconds) : null;
  // Warn 5 min before the end, but fall back to 1 min for short (<6 min) plans
  // so the warning doesn't fire the moment the call connects.
  const warnAt = total > 6 * 60 ? 5 * 60 : 60;
  const lowTime = remaining !== null && remaining <= warnAt;
  const warnMinutes = Math.round(warnAt / 60);

  // Lawyer's legal categories — the info block shows the first plus a "+N" chip.
  const categories =
    call.lawyer?.categories?.length
      ? call.lawyer.categories
      : call.lawyer?.specialization
      ? [call.lawyer.specialization]
      : [];

  const openChat = () => {
    setChatOpen(true);
    setUnread(0);
  };

  useEffect(() => {
    if (status !== "connected") return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  // Show the "time remaining" warning once, when the countdown crosses the
  // threshold; it auto-dismisses after a few seconds.
  useEffect(() => {
    if (remaining === null || warnedRef.current) return;
    if (remaining <= warnAt && remaining > 0) {
      warnedRef.current = true;
      setWarnVisible(true);
      const t = setTimeout(() => setWarnVisible(false), 7000);
      return () => clearTimeout(t);
    }
  }, [remaining, warnAt]);

  // Auto-end the call the moment the paid time runs out.
  useEffect(() => {
    if (total > 0 && remaining === 0 && !endedRef.current) {
      endedRef.current = true;
      onEnd();
    }
  }, [remaining, total, onEnd]);

  // Close the speaker-output menu when clicking anywhere outside it.
  useEffect(() => {
    if (!spkMenuOpen) return;
    const onDoc = (e) => { if (!e.target.closest(".lp-spk-wrap")) setSpkMenuOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [spkMenuOpen]);

  // Play the local camera preview imperatively into its own container (the same
  // pattern the lawyer side uses). Using the generic VideoPlayer here left the
  // PIP black because its cleanup calls track.stop() — under React's re-render /
  // StrictMode cycles that could stop the preview even though the track is still
  // live and publishing. The track's lifecycle is owned by useAgora, so we only
  // (re)play it here and never stop it.
  useEffect(() => {
    const el = localRef.current;
    if (!el || !localVideoTrack || !camOn) return;
    try {
      localVideoTrack.play(el);
    } catch (e) {
      /* track not ready — will retry on next render */
    }
  }, [localVideoTrack, camOn]);

  const remoteVideoTrack = remoteUsers[0]?.videoTrack;

  return (
    <div className={chatOpen ? "lp-call-screen lp-chat-shown" : "lp-call-screen"}>
      <div className="lp-call-stage">
      <div className="lp-remote">
        {remoteVideoTrack ? (
          <VideoPlayer track={remoteVideoTrack} className="lp-remote-video" />
        ) : (
          <div className="lp-remote-placeholder">
            <div className="lp-ph-avatar">
              {call.lawyer?.picture ? (
                <img src={call.lawyer.picture} alt={call.lawyer?.name} />
              ) : (
                <i className="bi bi-person-fill" />
              )}
            </div>
            <div className="lp-ph-text">
              {status === "connected"
                ? "Connecting lawyer's video…"
                : status === "failed"
                ? error || "Could not start the call."
                : "Connecting…"}
            </div>
          </div>
        )}
      </div>

      <div className="lp-call-top">
        <span className={lowTime ? "lp-timer lp-timer-low" : "lp-timer"}>
          {remaining !== null ? fmt(remaining) : fmt(seconds)}
        </span>
        <span className="lp-enc"><span className="lp-enc-dot" /> Encrypted</span>
      </div>

      <img src={brandLogo} alt="Legal Platform" className="lp-call-logo" />

      {warnVisible && (
        <div className="lp-time-warn">
          <i className="bi bi-clock-history" />
          <span>{warnMinutes} {warnMinutes === 1 ? "minute" : "minutes"} remaining</span>
        </div>
      )}

      <div className={camOn && localVideoTrack ? "lp-pip" : "lp-pip lp-pip-off"}>
        <div ref={localRef} className="lp-pip-video" />
        <div className="lp-pip-off-label">
          <i className="bi bi-camera-video-off-fill" />
          <span>{camOn ? "Camera unavailable" : "Camera off"}</span>
        </div>
        <span className="lp-pip-dot" />
      </div>

      <div className="lp-call-info">
        <div className="lp-call-name">{call.lawyer?.name || "Lawyer"}</div>
        {call.lawyer?.rating > 0 && (
          <div className="lp-call-rating">
            <i className="bi bi-star-fill" />
            <span className="lp-call-rating-val">{call.lawyer.rating}</span>
            {call.lawyer?.total_reviews > 0 && (
              <span className="lp-call-rating-count">({call.lawyer.total_reviews} reviews)</span>
            )}
          </div>
        )}
        {categories.length > 0 && (
          <div className="lp-call-cats">
            <span className="lp-cat-chip">{categories[0]}</span>
            {categories.length > 1 && <span className="lp-cat-more">+{categories.length - 1}</span>}
          </div>
        )}
      </div>

      <div className="lp-call-controls">
        <div className="lp-call-ctrl">
          <button className={micOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleMic}>
            <i className={micOn ? "bi bi-mic-fill" : "bi bi-mic-mute-fill"} />
          </button>
          <span className="lp-call-ctrl-label">Mute</span>
        </div>
        <div className="lp-call-ctrl">
          <button className={camOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleCam}>
            <i className={camOn ? "bi bi-camera-video-fill" : "bi bi-camera-video-off-fill"} />
          </button>
          <span className="lp-call-ctrl-label">Camera</span>
        </div>
        {canFlip && (
          <div className="lp-call-ctrl">
            <button className="lp-call-btn" onClick={flipCamera} disabled={!camOn}>
              <i className="bi bi-arrow-repeat" />
            </button>
            <span className="lp-call-ctrl-label">Flip</span>
          </div>
        )}
        <div className="lp-call-ctrl">
          <div className="lp-spk-wrap">
            <button className={speakerOn ? "lp-call-btn" : "lp-call-btn lp-active"} onClick={toggleSpeaker}>
              <i className={speakerOn ? "bi bi-volume-up-fill" : "bi bi-volume-mute-fill"} />
            </button>
            {playbackDevices.length > 1 && (
              <button
                className="lp-spk-caret"
                onClick={() => setSpkMenuOpen((o) => !o)}
                title="Choose audio output"
              >
                <i className="bi bi-chevron-up" />
              </button>
            )}
            {spkMenuOpen && (
              <div className="lp-spk-menu">
                <div className="lp-spk-menu-head">Speaker output</div>
                {playbackDevices.map((d) => (
                  <button
                    key={d.deviceId}
                    className={d.deviceId === activeSpeaker ? "lp-spk-item lp-spk-item-on" : "lp-spk-item"}
                    onClick={() => { setPlayback(d.deviceId); setSpkMenuOpen(false); }}
                  >
                    <i className="bi bi-check2" />
                    <span>{d.label || "Output device"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <span className="lp-call-ctrl-label">Speaker</span>
        </div>
        <div className="lp-call-ctrl">
          <button
            className={chatOpen ? "lp-call-btn lp-active" : "lp-call-btn"}
            onClick={() => (chatOpen ? setChatOpen(false) : openChat())}
          >
            <i className="bi bi-chat-dots-fill" />
            {unread > 0 && <span className="lp-chat-badge">{unread > 9 ? "9+" : unread}</span>}
          </button>
          <span className="lp-call-ctrl-label">Chat</span>
        </div>
        <div className="lp-call-ctrl">
          <button className="lp-call-btn lp-call-end" onClick={() => setConfirmEnd(true)}>
            <i className="bi bi-telephone-x-fill" />
          </button>
          <span className="lp-call-ctrl-label">End Call</span>
        </div>
      </div>

      <div className="lp-call-secure">
        <i className="bi bi-shield-lock-fill" />
        <div className="lp-call-secure-text">
          <strong>Your call is secure and confidential</strong>
          <span>All conversations are end-to-end encrypted</span>
        </div>
      </div>

      {confirmEnd && (
        <div className="lp-confirm-overlay" onClick={() => setConfirmEnd(false)}>
          <div className="lp-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="lp-confirm-icon"><i className="bi bi-telephone-x-fill" /></div>
            <div className="lp-confirm-title">End this call?</div>
            <div className="lp-confirm-text">Are you sure you want to end the consultation call?</div>
            <div className="lp-confirm-actions">
              <button className="lp-confirm-cancel" onClick={() => setConfirmEnd(false)}>Cancel</button>
              <button className="lp-confirm-ok" onClick={onEnd}>End Call</button>
            </div>
          </div>
        </div>
      )}
      </div>

      <ChatPanel
        call={call}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        onIncoming={() => setUnread((n) => n + 1)}
      />
    </div>
  );
}
